import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { classNames } from "primereact/utils";
import styles from "./WaitingListConfirmation.module.scss";
import { routePaths } from "../../../common/infrastructure/routes/routePaths";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import { getErrorToast } from "../../../common/infrastructure/utilities/toastUtility";
import { useAppContext } from "../../../AppContext";
import {
  PrepaymentDialog,
  IPrepaymentDialogConfirmResult,
} from "../../../common/components/PrepaymentDialog/PrepaymentDialog";
import { PrepaymentProvider } from "../../../common/enums";
import {
  IConfirmWaitingListSpotAndPayPayload,
  IConfirmWaitingListSpotPayload,
  IConfirmWaitingListSpotResultModel,
} from "./models/waitingListConfirmationModels";
import {
  WaitingListConflict,
  WaitingListConflictKept,
  WaitingListConflictLogin,
  WaitingListConflictWrongAccount,
} from "./components/WaitingListConflict";

type ConfirmationStatus =
  | "loading"
  | "success"
  | "spot_taken"
  | "awaiting_payment"
  | "processing_payment"
  | "pending_link_payment"
  // The client already has a reservation that clashes with this class:
  | "conflict" // pick which one to keep
  | "conflict_login" // anonymous link: log in first to see the details and choose
  | "conflict_wrong_account" // logged in, but not as the waitlisted client
  | "conflict_kept"; // kept the existing reservation, still on the waiting list

interface PaymentChoiceData {
  amount: number;
  eventId: number;
  prepaymentProvider: PrepaymentProvider;
}

export const WaitingListConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ConfirmationStatus>("loading");
  const [celebrationActive, setCelebrationActive] = useState(false);
  const [paymentChoice, setPaymentChoice] = useState<PaymentChoiceData | null>(null);
  const [showPrepaymentDialog, setShowPrepaymentDialog] = useState(false);
  const { selectedStudioId, isDesktopOrTablet, isAuthenticated } = useAppContext();
  const hasConfirmed = useRef(false);
  // The latest conflict response, rendered by the conflict views.
  const [conflictResult, setConflictResult] =
    useState<IConfirmWaitingListSpotResultModel | null>(null);
  // The client chose to take the spot and cancel the clashing reservation(s); the later
  // pay call must send the same choice or the backend reports the conflict again.
  const [replaceConflicts, setReplaceConflicts] = useState(false);
  // The confirmation really cancelled another booking (not just asked to): shown on success.
  const [cancelledPrevious, setCancelledPrevious] = useState(false);
  // Set by the inline login; the confirm is re-sent once the new session is in place.
  const [recheckAfterLogin, setRecheckAfterLogin] = useState(false);

  const email = searchParams.get("email");
  const eventId = searchParams.get("eventId");

  const showConflict = (result: IConfirmWaitingListSpotResultModel) => {
    setConflictResult(result);
    setReplaceConflicts(false);
    if (result.RequiresLogin) {
      setStatus(isAuthenticated ? "conflict_wrong_account" : "conflict_login");
    } else {
      setStatus("conflict");
    }
  };

  const { mutate } = useApiMutation({
    onSuccess: (data: unknown) => {
      const result = data as IConfirmWaitingListSpotResultModel;
      if (result.HasConflictingReservations) {
        showConflict(result);
      } else if (result.Success && result.RequiresStripePayment) {
        setPaymentChoice({
          amount: result.Amount ?? 0,
          eventId: result.EventId ?? Number(eventId),
          prepaymentProvider: PrepaymentProvider.Stripe,
        });
        setStatus("awaiting_payment");
      } else if (result.Success && result.RequiresLinkPayment) {
        // Link prepayment now goes through the same payment-method dialog as Stripe —
        // the dialog hands off to either plan payment or the existing link-email flow.
        setPaymentChoice({
          amount: result.Amount ?? 0,
          eventId: result.EventId ?? Number(eventId),
          prepaymentProvider: PrepaymentProvider.Link,
        });
        setStatus("awaiting_payment");
      } else if (result.Success) {
        setCancelledPrevious(!!result.CancelledReservationIds?.length);
        setStatus("success");
        setTimeout(() => {
          setCelebrationActive(true);
        }, 500);
      } else if (result.Message?.includes("заето")) {
        setStatus("spot_taken");
      } else {
        getErrorToast(
          result.Message || "Нещо се обърка, моля опитайте по-късно",
        );
        setTimeout(() => {
          navigate(routePaths.portalAppointment);
        }, 2000);
      }
    },
    onError: () => {
      getErrorToast("Нещо се обърка, моля опитайте по-късно");
      setTimeout(() => {
        navigate(routePaths.portalAppointment);
      }, 2000);
    },
  });

  // Tracks the active payment attempt so we know how to interpret a Success without StripeUrl:
  //   - plan-paid → reservation is fully confirmed (no redirect)
  //   - card → BE *should* return StripeUrl; missing it is a Stripe-integration error
  const [paidByPlan, setPaidByPlan] = useState(false);

  const { mutate: confirmAndPay } = useApiMutation({
    onSuccess: (data: unknown) => {
      const result = data as IConfirmWaitingListSpotResultModel;
      if (result.HasConflictingReservations) {
        showConflict(result);
      } else if (result.Success && result.StripeCheckoutUrl) {
        window.location.href = result.StripeCheckoutUrl;
      } else if (result.Success && result.RequiresLinkPayment) {
        // Link card path: spot is now Pending, payment-link email already sent.
        setStatus("pending_link_payment");
      } else if (result.Success && !result.StripeCheckoutUrl) {
        if (paidByPlan) {
          // Plan-paid: reservation is already confirmed, no redirect needed.
          setCancelledPrevious(!!result.CancelledReservationIds?.length);
          setStatus("success");
          setTimeout(() => setCelebrationActive(true), 500);
        } else {
          getErrorToast(
            "Възникна грешка със Stripe интеграцията. Свържете се с администратор на обекта.",
          );
        }
      } else if (result.Message?.includes("заето")) {
        setStatus("spot_taken");
      } else {
        getErrorToast(
          result.Message || "Нещо се обърка, моля опитайте по-късно",
        );
        setTimeout(() => {
          navigate(routePaths.portalAppointment);
        }, 2000);
      }
    },
    onError: () => {
      getErrorToast("Нещо се обърка, моля опитайте по-късно");
      setTimeout(() => {
        navigate(routePaths.portalAppointment);
      }, 2000);
    },
  });

  useEffect(() => {
    if (hasConfirmed.current || !selectedStudioId) {
      return;
    }

    if (!email || !eventId) {
      getErrorToast("Нещо се обърка, моля опитайте по-късно");
      setTimeout(() => {
        navigate(routePaths.portalAppointment);
      }, 2000);
      return;
    }

    hasConfirmed.current = true;

    sendConfirm(false);
  }, [selectedStudioId]);

  // After the inline login: re-send the confirm (no replace) to get the conflict details.
  // This runs on the render that already has the new user, so useApiMutation sends the
  // new token (it sets it from the current user right before each request).
  useEffect(() => {
    if (!recheckAfterLogin || !isAuthenticated) {
      return;
    }

    setRecheckAfterLogin(false);
    setStatus("loading");
    sendConfirm(false);
  }, [recheckAfterLogin, isAuthenticated]);

  function sendConfirm(replaceConflictingReservations: boolean) {
    if (!email || !eventId) return;

    const payload: IConfirmWaitingListSpotPayload = {
      email,
      eventId: Number(eventId),
      replaceConflictingReservations,
    };

    mutate({
      endpoint: endpointsMetadata.portalCalendar.confirmWaitingListSpot,
      method: "post",
      globalIsLoading: false,
      disableErrorToast: true,
      disableSuccessToast: true,
      payload,
    });
  }

  const handleReplaceConflicts = () => {
    setReplaceConflicts(true);
    setStatus("loading");
    sendConfirm(true);
  };

  const handleKeepExisting = () => {
    setStatus("conflict_kept");
  };

  const handleConflictLoginSuccess = () => {
    setRecheckAfterLogin(true);
  };

  const handleProceedToPayment = () => {
    if (!email || !paymentChoice?.eventId) return;
    setShowPrepaymentDialog(true);
  };

  const handlePrepaymentConfirm = (result: IPrepaymentDialogConfirmResult) => {
    if (!email || !paymentChoice?.eventId) return;

    setShowPrepaymentDialog(false);
    setPaidByPlan(result.subscriptionPlanId !== null);
    setStatus("processing_payment");

    const payload: IConfirmWaitingListSpotAndPayPayload = {
      email,
      eventId: paymentChoice.eventId,
      subscriptionPlanId: result.subscriptionPlanId,
      replaceConflictingReservations: replaceConflicts,
    };

    confirmAndPay({
      endpoint: endpointsMetadata.portalCalendar.confirmWaitingListSpotAndPay,
      method: "post",
      globalIsLoading: false,
      disableErrorToast: true,
      disableSuccessToast: true,
      payload,
    });
  };

  const handlePrepaymentCancel = () => {
    setShowPrepaymentDialog(false);
  };

  const handleDecline = () => {
    navigate(routePaths.portalAppointment);
  };

  const handleGoToCalendar = () => {
    navigate(routePaths.calendar);
  };

  const handleGoToMyReservations = () => {
    navigate(routePaths.portalMyReservations);
  };

  if (status === "loading" || status === "processing_payment") {
    return (
      <div className={styles.container}>
        <div className={styles.loadingAnimation}>
          <div className={styles.spinner}></div>
          <h2 className={styles.loadingTitle}>
            {status === "processing_payment"
              ? "Подготвяме плащането..."
              : "Потвърждаваме вашето място..."}
          </h2>
          <p className={styles.loadingSubtitle}>Моля, изчакайте...</p>
        </div>
      </div>
    );
  }

  if (status === "conflict" && conflictResult) {
    return (
      <WaitingListConflict
        result={conflictResult}
        onReplace={handleReplaceConflicts}
        onKeep={handleKeepExisting}
      />
    );
  }

  if (status === "conflict_login") {
    return (
      <WaitingListConflictLogin
        onLoginSuccess={handleConflictLoginSuccess}
        returnUrl={`${routePaths.confirmWaitingList}?${searchParams.toString()}`}
      />
    );
  }

  if (status === "conflict_wrong_account") {
    return (
      <WaitingListConflictWrongAccount
        onGoToMyReservations={handleGoToMyReservations}
        onGoToCalendar={handleGoToCalendar}
      />
    );
  }

  if (status === "conflict_kept") {
    return (
      <WaitingListConflictKept
        result={conflictResult}
        onGoToMyReservations={handleGoToMyReservations}
        onGoToCalendar={handleGoToCalendar}
      />
    );
  }

  if (status === "awaiting_payment") {
    return (
      <div className={styles.container}>
        <PrepaymentDialog
          visible={showPrepaymentDialog}
          eligiblePlansEndpoint={
            email && paymentChoice?.eventId
              ? `${endpointsMetadata.portalCalendar.eligiblePlansForWaitingList}?email=${encodeURIComponent(
                  email,
                )}&eventId=${paymentChoice.eventId}`
              : undefined
          }
          eligiblePlansQueryKey={[
            endpointsMetadata.portalCalendar.eligiblePlansForWaitingList,
            paymentChoice?.eventId,
            email,
          ]}
          price={paymentChoice?.amount ?? 0}
          prepaymentProvider={paymentChoice?.prepaymentProvider}
          isDesktopOrTablet={isDesktopOrTablet}
          isPrepaid
          onConfirm={handlePrepaymentConfirm}
          onCancel={handlePrepaymentCancel}
        />

        <div className={styles.successAnimation}>
          <div className={styles.successIcon}>
            <span className={styles.checkmark}>💳</span>
          </div>

          <h1 className={styles.successTitle}>Освободи се място!</h1>
          <p className={styles.successSubtitle}>
            За да запазите мястото си, е необходимо да заплатите{" "}
            <strong>{paymentChoice?.amount?.toFixed(2)} EUR</strong>.
          </p>
        </div>

        <div className={styles.actionsSection}>
          <div className={styles.primaryActions}>
            <button
              className={styles.primaryButton}
              onClick={handleProceedToPayment}
              type="button"
            >
              <span className={styles.buttonIcon}>💳</span>
              <span className={styles.buttonText}>Плати и запази мястото</span>
            </button>

            <button
              className={styles.secondaryButton}
              onClick={handleDecline}
              type="button"
            >
              <span className={styles.buttonIcon}>✕</span>
              <span className={styles.buttonText}>Отказ</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className={styles.container}>
        {/* Success Animation */}
        <div className={styles.successAnimation}>
          <div
            className={classNames(styles.successIcon, {
              [styles.celebrating]: celebrationActive,
            })}
          >
            <span className={styles.checkmark}>✓</span>

            {/* Celebration particles */}
            {celebrationActive && (
              <>
                <div
                  className={styles.particle}
                  style={
                    {
                      "--delay": "0.1s",
                      "--x": "-20px",
                      "--y": "-30px",
                    } as React.CSSProperties
                  }
                >
                  🎉
                </div>
                <div
                  className={styles.particle}
                  style={
                    {
                      "--delay": "0.2s",
                      "--x": "25px",
                      "--y": "-25px",
                    } as React.CSSProperties
                  }
                >
                  ✨
                </div>
                <div
                  className={styles.particle}
                  style={
                    {
                      "--delay": "0.3s",
                      "--x": "-30px",
                      "--y": "20px",
                    } as React.CSSProperties
                  }
                >
                  🎊
                </div>
                <div
                  className={styles.particle}
                  style={
                    {
                      "--delay": "0.4s",
                      "--x": "30px",
                      "--y": "25px",
                    } as React.CSSProperties
                  }
                >
                  ⭐
                </div>
                <div
                  className={styles.particle}
                  style={
                    {
                      "--delay": "0.5s",
                      "--x": "0px",
                      "--y": "-40px",
                    } as React.CSSProperties
                  }
                >
                  🎈
                </div>
              </>
            )}
          </div>

          <h1 className={styles.successTitle}>Ще ви очакваме! 🎉</h1>
          <p className={styles.successSubtitle}>
            Вашата резервация е потвърдена! Ще получите имейл за потвърждение.
            {cancelledPrevious && " Предишната ви резервация е отменена."}
          </p>
        </div>

        {/* Action Buttons */}
        <div className={styles.actionsSection}>
          <div className={styles.primaryActions}>
            <button
              className={styles.primaryButton}
              onClick={handleGoToCalendar}
              type="button"
            >
              <span className={styles.buttonIcon}>📅</span>
              <span className={styles.buttonText}>Към календара</span>
            </button>

            <button
              className={styles.secondaryButton}
              onClick={handleGoToMyReservations}
              type="button"
            >
              <span className={styles.buttonIcon}>📊</span>
              <span className={styles.buttonText}>Моите резервации</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === "pending_link_payment") {
    return (
      <div className={styles.container}>
        <div className={styles.successAnimation}>
          <div className={styles.successIcon}>
            <span className={styles.checkmark}>💳</span>
          </div>

          <h1 className={styles.successTitle}>Мястото ви е запазено!</h1>
          <p className={styles.successSubtitle}>
            Резервацията ви е в очакване на плащане. Ще получите имейл с
            инструкции за плащане.
          </p>
        </div>

        <div className={styles.actionsSection}>
          <div className={styles.primaryActions}>
            <button
              className={styles.primaryButton}
              onClick={handleGoToMyReservations}
              type="button"
            >
              <span className={styles.buttonIcon}>📊</span>
              <span className={styles.buttonText}>Моите резервации</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === "spot_taken") {
    return (
      <div className={styles.container}>
        <div className={styles.errorAnimation}>
          <div className={styles.errorIcon}>
            <span className={styles.cross}>✕</span>
          </div>

          <h1 className={styles.errorTitle}>Съжаляваме!</h1>
          <p className={styles.errorSubtitle}>
            Мястото вече е заето от друг потребител.
          </p>
          <p className={styles.errorMessage}>
            Опитайте да се запишете за друг клас или се свържете с обекта за
            повече информация.
          </p>
        </div>

        {/* Action Buttons */}
        <div className={styles.actionsSection}>
          <div className={styles.primaryActions}>
            <button
              className={styles.primaryButton}
              onClick={handleGoToCalendar}
              type="button"
            >
              <span className={styles.buttonIcon}>📅</span>
              <span className={styles.buttonText}>Към календара</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
